import React, { useCallback, useEffect, useState } from 'react';
import QRCode from 'react-qr-code';
import { useHistory } from 'react-router-dom';
import { decodeToken, generatePublicKey } from 'helpers/terminal';
import PropTypes from 'prop-types';

import {
  AccountManager,
  isUserNeedVerificationTerminals,
  Kyc,
  NotificationManager,
  StorageManager,
  TerminalApi,
  VerificationManager,
} from '@atlas/core';
import { Label, Loader } from '@atlas/ui';

import { SIGN_IN_REDIRECT_URL_QUERY_KEY, TERMINAL_STORAGE_KEY } from 'common/constants';
import TerminalQrCodePopup from 'src/terminal/popups/TerminalQrCodePopup';

import { AuthorizedRedirect } from '../components/NotAuthorizedRedirect';

const getTextSignUp = isSignUp => {
  if (isSignUp) {
    return {
      loading: 'terminal_registration_loading_text',
      text: 'terminal_registration_text',
      title: 'terminal_registration_title',
    };
  }

  return {
    loading: 'terminal_login_loading_text',
    text: 'terminal_login_text',
    title: 'terminal_login_title',
  };
};

const TerminalSigninPopup = ({ isSignUp }) => {
  const [ttl, setTtl] = useState(0);
  const [counterToHide, setCounterToHide] = useState(0);
  const [sessionUid, setSessionUid] = useState('');
  const [isLoading, setLoading] = useState('');
  const labels = getTextSignUp(isSignUp);
  const link =
    sessionUid &&
    `${window.MobileMainLink}${isSignUp ? '/signup' : '/signin'}?sessionUid=${sessionUid}&sessionUidTtl=${ttl}`;

  const {
    location: { pathname, search },
    push,
    replace,
  } = useHistory();

  const customHandleClose = useCallback((path = pathname) => push(path), [pathname, push]);

  const terminalKycVerification = async client => {
    const { type: provider } = await Kyc.getKycVerificationType();
    const { initAttributes } = await Kyc.initKycProvider({
      cpf: client.docNumber,
      kycType: provider,
    });

    const options = {
      attributes: initAttributes,
      cpf: client.docNumber,
      provider,
    };

    await VerificationManager.startVerification(options);

    NotificationManager.notify({
      message: 'withdrawal_verification_completed_successfully',
      status: 'success',
      type: 'notification',
    });
  };

  useEffect(() => {
    let interval;
    if (counterToHide === 1) {
      interval && clearTimeout(interval);
      customHandleClose();
    } else if (sessionUid && counterToHide > 0) {
      interval = setTimeout(() => {
        setCounterToHide(counterToHide - 1);
        interval && clearTimeout(interval);
        if (!isLoading && (counterToHide === 60 || (counterToHide - 1) % 4 === 0)) {
          if (isLoading) return;
          setLoading(true);
          TerminalApi.loginTerminalStatus({ uid: sessionUid })
            .then(({ status, token } = {}) => {
              setLoading(false);
              if (status === 'FAILED') {
                interval && clearTimeout(interval);
                customHandleClose();
              } else if (status === 'SUCCESS' && token) {
                decodeToken(token).then(decodedToken => {
                  interval && clearTimeout(interval);
                  if (!decodedToken) {
                    customHandleClose();
                  } else {
                    setCounterToHide(0);
                    AccountManager.loginWithToken(decodedToken)
                      .then(async ({ client }) => {
                        const params = new URLSearchParams(search);
                        const redirectUrl = params.get(SIGN_IN_REDIRECT_URL_QUERY_KEY);
                        if (redirectUrl) {
                          replace(redirectUrl);
                        } else {
                          replace('/');
                        }

                        if (isUserNeedVerificationTerminals(client)) {
                          await terminalKycVerification(client);
                        }
                      })
                      .catch(() => customHandleClose());
                  }
                });
              }
            })
            .catch(() => {});
        }
      }, 1000);
    }
    return () => interval && clearTimeout(interval);
  }, [counterToHide, isLoading, customHandleClose, search, replace, sessionUid]);

  useEffect(() => {
    const secret = StorageManager.getItem(TERMINAL_STORAGE_KEY);
    if (!secret) {
      window.location.href = '/';
      return;
    }

    generatePublicKey().then(publicKey => {
      const requestData = {
        publicKey,
        secret,
      };

      TerminalApi.generateLink(requestData)
        .then(({ sessionUid, ttl } = {}) => {
          setTtl(ttl);
          setCounterToHide(ttl);
          setSessionUid(sessionUid);
        })
        .catch(() => {
          StorageManager.removeItem(TERMINAL_STORAGE_KEY);
          window.location.href = '/';
        });
    });
  }, [replace]);

  return (
    <>
      {!sessionUid && <AuthorizedRedirect to={pathname} />}
      <TerminalQrCodePopup
        className="terminal-signin-modal"
        customHandleClose={customHandleClose}
        footer={
          <div className="terminal-loading">
            <Loader className="terminal-loading-icon" />
            <Label className="terminal-loading-text" message={labels.loading} />
          </div>
        }
        qrCode={!!sessionUid && <QRCode size={240} value={link} />}
        text={labels.text}
        title={labels.title}
      />
    </>
  );
};

TerminalSigninPopup.propTypes = {
  isSignUp: PropTypes.bool,
};

export default TerminalSigninPopup;
